<section class="gap-2 py-4 pr-4 pl-6" aria-labelledby="ticket-list-heading">
  <div class="pb-1">
    <!-- Page header: Title + Actions -->
    <div
      class="lg2:flex-row lg2:items-center lg2:justify-between lg2:gap-4 flex flex-col items-stretch gap-3 pb-1"
    >
      <div class="flex items-center gap-2">
        <mat-icon
          fontSet="material-icons-outlined"
          class="mt-0.5"
          aria-hidden="true"
          >build</mat-icon
        >
        <h1 id="ticket-list-heading" class="flex items-center gap-2">
          Tickets
          @if (isLoading) {
          <span
            class="font-lato inline-flex h-6 min-w-6 animate-pulse items-center justify-center rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700"
            aria-hidden="true"
          ></span>
        } @else {
          <span
            class="font-lato inline-flex min-w-6 items-center justify-center rounded-full bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700"
            [attr.aria-label]="(totalTickets() || 0) + ' tickets total'"
            >{{ totalTickets() || 0 | number }}</span
          >
        }
        </h1>
      </div>
      <div
        class="lg2:flex lg2:grid-cols-none lg2:items-center grid grid-cols-2 gap-2"
      >
        <div
          class="lg2:[&_button]:h-12 lg2:[&_button]:w-fit lg2:[&_button]:px-6 lg2:[&_button]:text-sm [&_button]:h-11 [&_button]:w-full [&_button]:px-3 [&_button]:text-xs"
        >
          <app-csv-download
            [csvData]="csvData()"
            [fileName]="'Tickets.csv'"
            variant="subtle"
            [ariaLabel]="'Export Tickets CSV'"
          ></app-csv-download>
        </div>
        @if (canCreateTicket() !== false) {
          <button
            class="btn-primary lg2:h-12 lg2:w-fit lg2:px-6 lg2:text-sm h-11 w-full px-3 text-xs"
            *ngxPermissionsOnly="'Ticketing: Write'"
            (click)="openDialog()"
          >
            Create Ticket
          </button>
        }
      </div>
    </div>

    <!-- Row 1: Search -->
    <div class="flex py-2">
      <!-- Search -->
      <label for="ticket-search" class="sr-only"
        >Search tickets by case number, title, location, or service</label
      >
      <div class="relative w-full">
        <mat-icon
          class="pointer-events-none absolute top-1/2 left-3 flex! h-4! w-4! -translate-y-1/2 items-center! justify-center! text-lg! text-black"
          aria-hidden="true"
          >search</mat-icon
        >
        <input
          id="ticket-search"
          type="search"
          class="comcast-input mt-0! pl-9 text-sm!"
          [formControl]="searchTermControl"
          placeholder="Search by case number..."
          autocomplete="off"
          aria-describedby="ticket-results-status"
        />
      </div>
    </div>

    <!-- Row 2: Filter dropdowns + Status pills -->
    <div class="flex flex-wrap items-center justify-between gap-2">
      <div
        class="lg2:flex lg2:w-auto lg2:flex-wrap lg2:items-center grid w-full grid-cols-2 gap-2"
      >
        <!-- Group by -->
        <div class="relative">
          <button
            id="groupBySelect"
            type="button"
            class="font-lato lg2:w-auto inline-flex w-full items-center justify-between gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:outline-none"
            [ngClass]="
              isGroupByOpen()
                ? 'border-blue-400 text-black'
                : 'hover:bg-gray-75 border-gray-400 bg-white text-black hover:border-gray-700'
            "
            [attr.aria-expanded]="isGroupByOpen()"
            aria-controls="ticket-group-by-options"
            aria-haspopup="listbox"
            (click)="openGroupByDropdown($event)"
          >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >folder_open</mat-icon
            >
            <span
              >Group: <strong>{{ currentGroupByLabel }}</strong></span
            >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >{{
                isGroupByOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
              }}</mat-icon
            >
          </button>
          @if (isGroupByOpen()) {
            <div
              id="ticket-group-by-options"
              class="comcast-select-panel"
              [class.comcast-select-panel--open]="isGroupByOpen()"
              role="listbox"
              aria-label="Group by options"
              (click)="$event.stopPropagation()"
            >
              <div class="comcast-select-options">
                @for (option of ['Type', 'Location', 'Status']; track option) {
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      currentGroupByLabel === option
                    "
                    role="option"
                    [attr.aria-selected]="currentGroupByLabel === option"
                    (click)="selectGroupBy(option)"
                  >
                    {{ option }}
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <!-- Sort -->
        <div class="relative">
          <button
            type="button"
            class="font-lato lg2:w-auto inline-flex w-full items-center justify-between gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:outline-none"
            [ngClass]="
              isSortOpen()
                ? 'border-blue-400 text-black'
                : 'hover:bg-gray-75 border-gray-400 bg-white text-black hover:border-gray-700'
            "
            [attr.aria-expanded]="isSortOpen()"
            aria-controls="ticket-sort-options"
            aria-haspopup="listbox"
            (click)="openSortDropdown($event)"
          >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >sort</mat-icon
            >
            <span
              ><strong>{{ currentSortLabel }}</strong></span
            >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >{{
                isSortOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
              }}</mat-icon
            >
          </button>
          @if (isSortOpen()) {
            <div
              id="ticket-sort-options"
              class="comcast-select-panel"
              [class.comcast-select-panel--open]="isSortOpen()"
              role="listbox"
              aria-label="Sort options"
              (click)="$event.stopPropagation()"
            >
              <div class="comcast-select-options">
                @for (option of sortOptions; track option.value) {
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      selectedSort === option.value
                    "
                    role="option"
                    [attr.aria-selected]="selectedSort === option.value"
                    (click)="selectSort(option.value)"
                  >
                    {{ option.label }}
                  </button>
                }
              </div>
            </div>
          }
        </div>

        <!-- Time range -->
        <div class="lg2:col-span-1 lg2:w-auto relative col-span-2 w-1/2">
          <button
            type="button"
            class="font-lato lg2:w-auto inline-flex w-full items-center justify-between gap-1.5 rounded-sm border px-3 py-1.5 text-sm transition-colors focus:border-blue-400 focus:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:outline-none"
            [ngClass]="
              isTimeRangeOpen()
                ? 'border-blue-400 text-black'
                : 'hover:bg-gray-75 border-gray-400 bg-white text-black hover:border-gray-700'
            "
            [attr.aria-expanded]="isTimeRangeOpen()"
            aria-controls="ticket-time-range-options"
            aria-haspopup="listbox"
            (click)="openTimeRangeDropdown($event)"
          >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >schedule</mat-icon
            >
            <span
              ><strong>{{ currentTimeRangeLabel }}</strong></span
            >
            <mat-icon
              class="flex! h-4! w-4! items-center! justify-center! text-base!"
              aria-hidden="true"
              >{{
                isTimeRangeOpen() ? 'arrow_drop_up' : 'arrow_drop_down'
              }}</mat-icon
            >
          </button>
          @if (isTimeRangeOpen()) {
            <div
              id="ticket-time-range-options"
              class="comcast-select-panel"
              [class.comcast-select-panel--open]="isTimeRangeOpen()"
              role="listbox"
              aria-label="Time range options"
              (click)="$event.stopPropagation()"
            >
              <div class="comcast-select-options">
                @for (opt of timeRangeOptions; track opt.value) {
                  <button
                    type="button"
                    class="comcast-select-option w-full text-left"
                    [class.comcast-select-option--selected]="
                      selectedTimeRange() === opt.value
                    "
                    role="option"
                    [attr.aria-selected]="selectedTimeRange() === opt.value"
                    (click)="selectTimeRange(opt.value)"
                  >
                    {{ opt.label }}
                  </button>
                }
              </div>
            </div>
          }
        </div>
      </div>
      <!-- Status pills -->
      <div
        class="lg2:mt-0 lg2:flex lg2:w-auto lg2:items-center mt-2 grid w-full grid-cols-3 gap-2"
        role="group"
        aria-label="Filter tickets by status"
      >
        <button
          type="button"
          class="font-lato lg2:px-4 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
          [ngClass]="
            activeStatusFilter === 'all'
              ? 'bg-blue-700 text-white'
              : 'border border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50'
          "
          [attr.aria-pressed]="activeStatusFilter === 'all'"
          (click)="selectStatusFilter('all')"
        >
          All
          <span
            class="inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold"
            [ngClass]="
              activeStatusFilter === 'all'
                ? 'bg-white/20 text-white'
                : 'bg-gray-100 text-gray-600'
            "
            >{{ allCount() }}</span
          >
        </button>
        <button
          type="button"
          class="font-lato lg2:px-4 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
          [ngClass]="
            activeStatusFilter === 'open'
              ? 'border border-red-600 bg-red-50 text-red-600'
              : 'border border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50'
          "
          [attr.aria-pressed]="activeStatusFilter === 'open'"
          (click)="selectStatusFilter('open')"
        >
          Open
          <span
            class="inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold"
            [ngClass]="
              activeStatusFilter === 'open'
                ? 'bg-red-100 text-red-600'
                : 'bg-gray-100 text-gray-600'
            "
            >{{ openCount() }}</span
          >
        </button>
        <button
          type="button"
          class="font-lato lg2:px-4 inline-flex items-center justify-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700"
          [ngClass]="
            activeStatusFilter === 'closed'
              ? 'border border-green-700 bg-green-700/10 text-green-700'
              : 'border border-gray-400 bg-white text-black hover:border-gray-700 hover:bg-gray-50'
          "
          [attr.aria-pressed]="activeStatusFilter === 'closed'"
          (click)="selectStatusFilter('closed')"
        >
          Closed
          <span
            class="inline-flex min-w-5 items-center justify-center rounded-full px-1.5 text-xs font-semibold"
            [ngClass]="
              activeStatusFilter === 'closed'
                ? 'bg-green-700/20 text-green-700'
                : 'bg-gray-100 text-gray-600'
            "
            >{{ closedCount() }}</span
          >
        </button>
      </div>
    </div>

    <!-- Summary line -->
    <div class="flex flex-wrap items-center justify-end gap-x-4 gap-y-1 pt-6">
      <p class="font-lato text-brand-muted text-right text-xs leading-snug">
        {{ resultsSummary }}
      </p>
      @if (hasActiveFilters) {
        <button
          type="button"
          class="font-lato inline-flex shrink-0 items-center gap-0.5 text-xs font-semibold text-blue-600 hover:underline focus:outline-none"
          (click)="clearFilters()"
        >
          Clear all
          <mat-icon
            class="flex! h-3.5! w-3.5! items-center! justify-center! text-sm!"
            aria-hidden="true"
            >close</mat-icon
          >
        </button>
      }
    </div>
  </div>

  <!-- Ticket List -->
  <div class="min-h-100" aria-live="polite" [attr.aria-busy]="isLoading">
    <p
      id="ticket-results-status"
      class="sr-only"
      role="status"
      aria-atomic="true"
    >
      {{ filteredTotal() || 0 }} tickets shown.
    </p>
    @if (isLoading) {
      <div
        class="animate-pulse space-y-3"
        role="status"
        aria-label="Loading tickets"
      >
        @for (group of [0, 1]; track group) {
          <div
            class="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
          >
            <!-- Group header skeleton -->
            <div class="flex items-center justify-between px-4 py-3">
              <div class="flex items-center gap-3">
                <div class="h-8 w-8 rounded-md bg-gray-200"></div>
                <div class="h-4 w-28 rounded bg-gray-200"></div>
              </div>
              <div class="h-4 w-16 rounded bg-gray-200"></div>
            </div>
            <!-- Row skeletons -->
            @for (row of [0, 1, 2]; track row) {
              <div class="border-t border-gray-100 px-4 py-3">
                <div class="flex items-start justify-between gap-4">
                  <div class="min-w-0 flex-1 space-y-2">
                    <div class="h-3 w-20 rounded bg-gray-200"></div>
                    <div class="h-4 w-3/4 rounded bg-gray-200"></div>
                    <div class="h-3 w-1/2 rounded bg-gray-200"></div>
                  </div>
                  <div class="shrink-0 space-y-2">
                    <div
                      class="ml-auto h-5 w-14 rounded-full bg-gray-200"
                    ></div>
                    <div class="ml-auto h-3 w-12 rounded bg-gray-200"></div>
                  </div>
                </div>
              </div>
            }
          </div>
        }
        <span class="sr-only">Loading tickets…</span>
      </div>
    } @else if (groupedTickets.length === 0) {
      <div class="flex flex-col items-center justify-center py-16 text-center">
        <div
          class="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-gray-50"
          aria-hidden="true"
        >
          <mat-icon
            fontSet="material-icons-outlined"
            class="flex! h-12! w-12! items-center! justify-center! text-4xl! text-black"
            >receipt_long</mat-icon
          >
        </div>
        @if ((searchTermControl?.value?.length || 0) > 0) {
          <p class="font-montserrat text-lg font-semibold">No results found</p>
          <p
            class="font-lato mt-1 max-w-55 text-sm leading-relaxed text-gray-400"
          >
            No tickets match your search. Try different keywords.
          </p>
        } @else if (hasActiveFilters) {
          <p class="font-montserrat text-lg font-semibold">
            No tickets match your filters
          </p>
          <p
            class="font-lato mt-1 max-w-60 text-sm leading-relaxed text-gray-400"
          >
            Try adjusting or clearing your filters to see more tickets.
          </p>
          <button
            type="button"
            class="font-lato mt-5 inline-flex items-center gap-1 text-sm font-semibold text-blue-600 hover:underline focus:outline-none"
            (click)="clearFilters()"
          >
            Clear all filters
          </button>
        } @else {
          <p class="font-montserrat text-base font-semibold text-gray-700">
            No tickets yet
          </p>
          <p
            class="font-lato mt-1.5 max-w-55 text-sm leading-relaxed text-gray-400"
          >
            Tickets you create or that are assigned to your account will appear
            here.
          </p>
          @if (canCreateTicket() !== false) {
            <button
              class="btn-primary mt-5"
              *ngxPermissionsOnly="'Ticketing: Write'"
              (click)="openDialog()"
            >
              Create Ticket
            </button>
          }
        }
      </div>
    } @else {
      <div class="space-y-2" role="list" aria-label="Ticket groups">
        @for (
          ticketGroup of groupedTickets;
          track ticketGroup.key;
          let i = $index
        ) {
          @let cfg = getGroupConfig(ticketGroup.key);
          <div
            [id]="'ticket-group-' + ticketGroup.key"
            class="overflow-hidden rounded-sm bg-white shadow-sm"
            role="listitem"
          >
            <!-- Group header -->
            <button
              [id]="'ticket-group-header-' + i"
              type="button"
              class="hover:bg-blue-75 flex w-full items-center justify-between border border-white px-4 py-3 text-left transition-colors hover:border-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-inset"
              (click)="toggleGroup(ticketGroup.key)"
              [attr.aria-expanded]="isGroupExpanded(ticketGroup.key)"
              [attr.aria-controls]="'ticket-group-panel-' + i"
              [attr.aria-label]="
                cfg.label +
                (cfg.context ? ', ' + cfg.context : '') +
                ', ' +
                ticketGroup.tickets.length +
                (ticketGroup.tickets.length === 1 ? ' ticket' : ' tickets')
              "
            >
              <div class="flex items-center gap-3 py-2">
                <mat-icon
                  fontSet="material-icons-outlined"
                  class="text-brand-text flex! h-6! w-6! items-center! justify-center! text-2xl!"
                  [ngClass]="cfg.iconClass"
                  aria-hidden="true"
                  >{{ cfg.icon }}</mat-icon
                >
                <span class="font-lato flex items-center gap-2">
                  <span class="font-semibold text-black">{{ cfg.label }}</span>
                  @if (cfg.context) {
                    <span
                      class="text-brand-muted rounded-lg border border-gray-200 px-1.5 py-0.5 text-[11px] leading-none font-bold tracking-wide uppercase"
                    >
                      {{ cfg.context }}
                    </span>
                  }
                </span>
              </div>
              <div
                class="font-lato text-brand-muted flex items-center gap-2 text-sm"
              >
                <span
                  >{{ ticketGroup.tickets.length }}
                  {{
                    ticketGroup.tickets.length === 1 ? 'ticket' : 'tickets'
                  }}</span
                >
                <mat-icon
                  class="flex! h-6! w-6! items-center! justify-center! text-base! text-black"
                  aria-hidden="true"
                  >{{
                    isGroupExpanded(ticketGroup.key)
                      ? 'expand_less'
                      : 'chevron_right'
                  }}</mat-icon
                >
              </div>
            </button>

            <!-- Ticket rows -->
            @if (isGroupExpanded(ticketGroup.key)) {
              <div
                [id]="'ticket-group-panel-' + i"
                role="list"
                [attr.aria-labelledby]="'ticket-group-header-' + i"
              >
                <ng-template #ticketRowContent let-ticket="ticket">
                  @if (ticket.Id === selectedTicketId()) {
                    <div
                      class="absolute inset-y-0 left-0 hidden w-0.75 bg-blue-700 md:block"
                    ></div>
                  }
                  <div class="px-4 py-3">
                    <div class="flex items-start justify-between gap-4">
                      <!-- Left: case info -->
                      <div class="flex min-w-0 flex-1 items-start gap-2">
                        <span
                          class="mt-1.5 ml-1 h-2 w-2 shrink-0 rounded-full"
                          [ngClass]="getDotColor(ticket)"
                        ></span>
                        <div class="min-w-0">
                          <div
                            class="font-lato mb-0.5 text-sm font-semibold text-blue-700"
                          >
                            {{ ticket.CaseNumber }}
                          </div>
                          <div class="min-w-0">
                            <span
                              class="font-lato block truncate font-semibold text-black"
                              >{{ ticket.Subject }}</span
                            >
                          </div>
                          <div
                            class="font-lato text-brand-muted mt-0.5 truncate text-sm"
                          >
                            {{ getTicketMeta(ticket) }}
                          </div>
                          @if (isFinanceTicket(ticket) && ticket.Description) {
                            <div
                              class="font-lato text-brand-muted mt-0.5 truncate text-xs italic"
                            >
                              {{ ticket.Description }}
                            </div>
                          }
                        </div>
                      </div>
                      <!-- Right: status badge + time + comments -->
                      <div class="shrink-0 text-right">
                        <span
                          class="font-lato inline-block rounded-md border px-2 py-1 text-[11px] font-bold uppercase"
                          [ngClass]="{
                            'comcast-status-pill--success': ticket.IsClosed,
                            'comcast-status-pill--danger': !ticket.IsClosed,
                          }"
                          >{{ getStatusLabel(ticket) }}</span
                        >
                        <div class="font-lato text-brand-muted mt-1 text-xs">
                          {{
                            getRelativeTime(
                              ticket.IsClosed
                                ? ticket.ClosedDate
                                : ticket.CreatedDate
                            )
                          }}
                        </div>
                        @if (ticket.Comments__r?.totalSize > 0) {
                          <div
                            class="mt-1.5 flex items-center justify-end gap-1"
                          >
                            @if (
                              hasUnreadComments(
                                ticket.Comments__r?.records || []
                              )
                            ) {
                              <span
                                class="h-1.5 w-1.5 shrink-0 rounded-full bg-blue-700"
                                aria-label="Unread comments"
                              ></span>
                            }
                            <mat-icon
                              class="!flex !h-3.5 !w-3.5 !items-center !justify-center !text-sm"
                              [class.text-blue-700]="
                                hasUnreadComments(
                                  ticket.Comments__r?.records || []
                                )
                              "
                              [class.text-brand-muted]="
                                !hasUnreadComments(
                                  ticket.Comments__r?.records || []
                                )
                              "
                              aria-hidden="true"
                              >chat_bubble_outline</mat-icon
                            >
                            <span
                              class="font-lato text-xs"
                              [class.font-semibold]="
                                hasUnreadComments(
                                  ticket.Comments__r?.records || []
                                )
                              "
                              [class.text-blue-700]="
                                hasUnreadComments(
                                  ticket.Comments__r?.records || []
                                )
                              "
                              [class.text-brand-muted]="
                                !hasUnreadComments(
                                  ticket.Comments__r?.records || []
                                )
                              "
                              >{{ ticket.Comments__r.totalSize }}</span
                            >
                          </div>
                        }
                      </div>
                    </div>
                  </div>
                </ng-template>

                @for (ticket of ticketGroup.tickets; track ticket.Id) {
                  <a
                    [routerLink]="['/tickets', ticket.Id]"
                    class="hover:bg-blue-75/50 relative block cursor-pointer border-t border-gray-100 transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-700 focus-visible:ring-inset md:hidden"
                    role="listitem"
                    [attr.aria-label]="getTicketAriaLabel(ticket)"
                  >
                    <ng-container
                      [ngTemplateOutlet]="ticketRowContent"
                      [ngTemplateOutletContext]="{ ticket: ticket }"
                    ></ng-container>
                  </a>

                  <div
                    class="hover:bg-blue-75/50 relative hidden cursor-pointer border-t border-gray-100 transition-colors focus:outline-none md:block"
                    [class.bg-blue-75]="ticket.Id === selectedTicketId()"
                    tabindex="0"
                    role="listitem"
                    [attr.aria-current]="
                      ticket.Id === selectedTicketId() ? 'true' : null
                    "
                    [attr.aria-label]="getTicketAriaLabel(ticket)"
                    (click)="onTicketRowClick(ticket)"
                    (keydown.enter)="onTicketRowClick(ticket)"
                    (keydown.space)="
                      $event.preventDefault(); onTicketRowClick(ticket)
                    "
                  >
                    <ng-container
                      [ngTemplateOutlet]="ticketRowContent"
                      [ngTemplateOutletContext]="{ ticket: ticket }"
                    ></ng-container>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    }
  </div>
</section>
